import { Card, CardBody } from '@/components/ui/Card'
import type { BulkApplyTarget } from '@/lib/venue/priceViewModel'

/**
 * "다른 코트에도 동일하게 적용" 체크박스 + 대상 코트 다중 선택(요구 1, 설계 D2·D3).
 *
 * 🚨 **기본 미선택**이다 — 기본 전체 선택은 오조작 한 번으로 전 코트 요금을 덮어쓴다.
 * 🚨 적용 대상은 **지금 저장하는 축 하나**뿐이다(요일유형 탭이면 요일유형 요금, 날짜 탭이면 그 날짜).
 */
export function BulkApplyCourtField({
  courts,
  selectedIds,
  onChange,
  disabled,
  axisNotice,
}: {
  /** 현재 코트를 **제외한** 목록. 현재 코트는 항상 저장 대상이라 선택지가 아니다. */
  courts: BulkApplyTarget[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
  disabled: boolean
  /** 무엇이 복제되는지 알리는 문구(탭마다 다르다). */
  axisNotice: string
}) {
  const enabled = selectedIds.length > 0
  const allSelected = courts.length > 0 && selectedIds.length === courts.length

  if (courts.length === 0) return null

  return (
    <Card>
      <CardBody className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slateish-300 text-brand-500 focus:ring-brand-500"
            checked={enabled}
            disabled={disabled}
            onChange={(event) => {
              // 체크를 풀면 선택을 비운다 — 숨겨진 선택이 남아 저장 때 되살아나는 사고를 막는다.
              onChange(event.target.checked ? courts.map((court) => court.courtId) : [])
            }}
          />
          다른 코트에도 동일하게 적용
        </label>
        <p className="text-xs text-slateish-500">{axisNotice}</p>

        {enabled ? (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-slateish-600">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slateish-300 text-brand-500 focus:ring-brand-500"
                checked={allSelected}
                disabled={disabled}
                onChange={(event) => {
                  onChange(event.target.checked ? courts.map((court) => court.courtId) : [])
                }}
              />
              전체 선택
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {courts.map((court) => (
                <label
                  key={court.courtId}
                  className="flex items-center gap-2 rounded-lg border border-slateish-200 px-3 py-2 text-sm text-ink-700"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slateish-300 text-brand-500 focus:ring-brand-500"
                    checked={selectedIds.includes(court.courtId)}
                    disabled={disabled}
                    onChange={(event) => {
                      onChange(
                        event.target.checked
                          ? [...selectedIds, court.courtId]
                          : selectedIds.filter((id) => id !== court.courtId)
                      )
                    }}
                  />
                  {court.name}
                </label>
              ))}
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
